import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ModalCard, useCreateModal } from 'partial/components/Modal';
import FormMenu from 'partial/components/form/FormMenu';
import FormTextArea from 'partial/components/form/FormTextArea';
import Button from 'partial/components/Button';
import Form from 'partial/components/form';
import { useUpdateRequirement } from '../hooks';

export const useOpenApprovalModal = () => {
  const createModal = useCreateModal();

  const openApprovalModal = useCallback(
    (data) => {
      createModal({
        content: (close) => <ApprovalModal data={data} onClose={close} />,
      });
    },
    [createModal],
  );

  return openApprovalModal;
};

const templateOptions = [
  'The submitted documents are invalid',
  'The submitted documents are not readable',
];

const validationSchema = yup.object().shape({
  remarks: yup.string().min(1, 'Too short').max(500, 'Too long'),
});

const titleConfig = {
  approve: 'APPROVAL',
  reject: 'DISAPPROVAL',
};

function ApprovalModal({ data, onClose }) {
  const { cb } = data;
  const { type, organization } = data;
  const [template, setTemplate] = useState(null);
  const onSuccess = () => {
    cb();
    onClose();
  };
  const [isUpdating, update] = useUpdateRequirement(onSuccess, onClose);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: { remarks: '' },
    validationSchema,
    onSubmit: (payload) => {
      update({
        orgId: organization?.uuid,
        reqId: organization?.requirements?.uuid,
        action: type,
        payload: { remarks: payload?.remarks },
      });
    },
    validateOnChange: false,
  });

  useEffect(() => {
    if (!template) return;
    setFieldValue('remarks', template);
  }, [template, setFieldValue]);

  return (
    <ModalCard
      size="sm max-h-[90vh] overflow-y-auto"
      title={`CONFIRM ${titleConfig[type]}`}
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit} className="p-5 w-full">
        <div className="space-y-6">
          <div className="font-semibold text-neutral-500 text-sm">
            Are you sure you want to{' '}
            {type === 'approve' ? 'approve' : 'disapprove'} this user?
          </div>
          <div className="space-y-4">
            {type === 'reject' && (
              <FormMenu
                value={template}
                onChange={setTemplate}
                label="Select Template Message"
                options={templateOptions}
                className="sm:rounded-l-lg sm:rounded-none rounded-lg"
              />
            )}
            <FormTextArea
              name="remarks"
              onSetFieldValue={setFieldValue}
              value={values.remarks}
              error={errors.remarks}
              label="Remarks"
              required={type === 'reject'}
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={onClose}
                outline
                className="uppercase"
                disabled={isUpdating}
              >
                cancel
              </Button>
              <Button
                type="submit"
                primary
                className="uppercase"
                disabled={
                  type === 'reject'
                    ? values?.remarks?.length === 0 || isUpdating
                    : isUpdating
                }
              >
                confirm
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </ModalCard>
  );
}

ApprovalModal.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ApprovalModal;
